import LinearProgress from '@classify/react-core-components/LinearProgress';
import Box from '@classify/react-core-components/Box';
import { SxProps, Theme, styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';

export interface Props {
  variant: 'path' | 'project';
  maxValue: number;
  value: number;
  expectedValue?: number;
  isDisabled?: boolean;
  'aria-label': string;
  sx?: SxProps<Theme>;
}

function getPercent(value: number, maxValue: number) {
  return Math.min(100, Math.round((value * 100) / maxValue));
}

const ExpectedProgress = styled('div', {
  shouldForwardProp: (prop) => !['isDisabled'].includes(prop as string),
})<{ isDisabled: boolean }>(({ theme: { palette }, isDisabled }) => ({
  boxSizing: 'border-box',
  border: `1px dashed ${palette.progress.completion.active}`,
  backgroundColor: palette.progress.completion.expected,
  height: '100%',
  position: 'absolute',
  borderRadius: 4,
  top: 0,
  left: 0,
  zIndex: 2,
  ...(isDisabled && {
    borderColor: palette.progress.completion.disabled.active,
    backgroundColor: palette.progress.completion.disabled.expected,
  }),
}));

const LearningProgress = ({
  value,
  expectedValue = 0,
  maxValue,
  variant,
  isDisabled = false,
  'aria-label': ariaLabel,
  sx = {},
  ...otherProps
}: Props) => {
  const size = variant === 'path' ? 'large' : 'default';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
      <Box sx={{ position: 'relative', flexGrow: 1 }}>
        {expectedValue > 0 && (
          <ExpectedProgress
            isDisabled={isDisabled}
            sx={{
              width: `${getPercent(expectedValue, maxValue)}%`,
            }}
          />
        )}
        <LinearProgress
          aria-label={ariaLabel}
          shape="rounded"
          value={value}
          size={size}
          maxValue={maxValue}
          isDisabled={isDisabled}
          {...otherProps}
        />
      </Box>
      <Typography
        variant="bodySmall"
        sx={{
          marginLeft: 1,
          color: isDisabled ? 'progress.completion.disabled-active' : 'progress.completion.active',
        }}
      >
        {getPercent(value, maxValue)}%
      </Typography>
    </Box>
  );
};

export default LearningProgress;
